import React from 'react';
import { registerComponent } from './components/DynamicComponent';

/** Lazy-loaded module components — each route chunk loads on first visit. */
const lazy = <T extends Record<string, React.ComponentType<any>>>(
  loader: () => Promise<T>,
  exportName: keyof T
) =>
  React.lazy(() =>
    loader().then((mod) => ({ default: mod[exportName] as React.ComponentType<any> }))
  );

const Dashboard = lazy(() => import('./components/Modules/Dashboard'), 'Dashboard');
const Students = lazy(() => import('./components/Modules/Students'), 'Students');
const Teachers = lazy(() => import('./components/Modules/Teachers'), 'Teachers');
const TeacherPortal = lazy(() => import('./components/Modules/TeacherPortal'), 'TeacherPortal');
const Courses = lazy(() => import('./components/Modules/Courses'), 'Courses');
const Schedule = lazy(() => import('./components/Modules/Schedule'), 'Schedule');
const Grades = lazy(() => import('./components/Modules/Grades'), 'Grades');
const Settings = lazy(() => import('./components/Modules/Settings'), 'Settings');
const GenericSchoolModule = lazy(
  () => import('./components/Modules/GenericSchoolModule'),
  'GenericSchoolModule'
);
const Staff = lazy(() => import('./components/Modules/Staff'), 'Staff');
const StaffLeave = lazy(() => import('./components/Modules/StaffLeave'), 'StaffLeave');
const ApiExplorer = lazy(() => import('./components/Modules/ApiExplorer'), 'ApiExplorer');
const School = lazy(() => import('./components/Modules/School'), 'School');
const Library = lazy(() => import('./components/Modules/Library'), 'Library');
const ReportCards = lazy(() => import('./components/Modules/ReportCards'), 'ReportCards');
const Fees = lazy(() => import('./components/Modules/Fees'), 'Fees');
const Transport = lazy(() => import('./components/Modules/Transport'), 'Transport');
const Hostel = lazy(() => import('./components/Modules/Hostel'), 'Hostel');
const Certificates = lazy(() => import('./components/Modules/Certificates'), 'Certificates');
const Assignments = lazy(() => import('./components/Modules/Assignments'), 'Assignments');
const Procurement = lazy(() => import('./components/Modules/Procurement'), 'Procurement');
const Inventory = lazy(() => import('./components/Modules/Inventory'), 'Inventory');
const Canteen = lazy(() => import('./components/Modules/Canteen'), 'Canteen');
const Expenses = lazy(() => import('./components/Modules/Expenses'), 'Expenses');
const ParentConsentVault = lazy(
  () => import('./components/Modules/ParentConsentVault'),
  'ParentConsentVault'
);
const BoardReadiness = lazy(() => import('./components/Modules/BoardReadiness'), 'BoardReadiness');
const RiskInsights = lazy(() => import('./components/Modules/RiskInsights'), 'RiskInsights');
const TeachingAnalytics = lazy(
  () => import('./components/Modules/TeachingAnalytics'),
  'TeachingAnalytics'
);
const Permissions = lazy(() => import('./components/Modules/Permissions'), 'Permissions');
const Communication = lazy(() => import('./components/Modules/Communication'), 'Communication');
const Announcements = lazy(() => import('./components/Modules/Announcements'), 'Announcements');
const Security = lazy(() => import('./components/Modules/Security'), 'Security');
const IssuePickupPass = lazy(() => import('./components/Modules/IssuePickupPass'), 'IssuePickupPass');
const Holidays = lazy(() => import('./components/Modules/Holidays'), 'Holidays');
const SuperAdmin = lazy(() => import('./components/Modules/SuperAdmin'), 'SuperAdmin');
const ClassAttendance = lazy(
  () => import('./components/Modules/ClassAttendance'),
  'ClassAttendance'
);
const AiQuestions = lazy(() => import('./components/Modules/AiQuestions'), 'AiQuestions');
const StudentPortal = lazy(() => import('./components/Modules/StudentPortal'), 'StudentPortal');
const SessionPromotion = lazy(
  () => import('./components/Modules/SessionPromotion'),
  'SessionPromotion'
);
const Admissions = lazy(() => import('./components/Modules/Admissions'), 'Admissions');
const Website = lazy(() => import('./components/Modules/Website'), 'Website');

/** Register all UI modules once at app startup. */
export function registerAllModules(): void {
  registerComponent('Dashboard', Dashboard);
  registerComponent('Students', Students);
  registerComponent('Teachers', Teachers);
  registerComponent('TeacherPortal', TeacherPortal);
  registerComponent('Courses', Courses);
  registerComponent('Schedule', Schedule);
  registerComponent('Grades', Grades);
  registerComponent('Settings', Settings);
  registerComponent('GenericSchoolModule', GenericSchoolModule);
  registerComponent('Staff', Staff);
  registerComponent('StaffLeave', StaffLeave);
  registerComponent('ApiExplorer', ApiExplorer);
  registerComponent('School', School);
  registerComponent('Library', Library);
  registerComponent('ReportCards', ReportCards);
  registerComponent('Fees', Fees);
  registerComponent('Transport', Transport);
  registerComponent('Hostel', Hostel);
  registerComponent('Certificates', Certificates);
  registerComponent('Assignments', Assignments);
  registerComponent('Procurement', Procurement);
  registerComponent('Inventory', Inventory);
  registerComponent('Canteen', Canteen);
  registerComponent('Expenses', Expenses);
  registerComponent('ParentConsentVault', ParentConsentVault);
  registerComponent('BoardReadiness', BoardReadiness);
  registerComponent('RiskInsights', RiskInsights);
  registerComponent('TeachingAnalytics', TeachingAnalytics);
  registerComponent('Permissions', Permissions);
  registerComponent('Communication', Communication);
  registerComponent('Announcements', Announcements);
  registerComponent('Security', Security);
  registerComponent('IssuePickupPass', IssuePickupPass);
  registerComponent('Holidays', Holidays);
  registerComponent('SuperAdmin', SuperAdmin);
  registerComponent('ClassAttendance', ClassAttendance);
  registerComponent('AiQuestions', AiQuestions);
  registerComponent('StudentPortal', StudentPortal);
  registerComponent('SessionPromotion', SessionPromotion);
  registerComponent('Admissions', Admissions);
  registerComponent('Website', Website);
}
